import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { theme, useScrollspy } from '@sg/garnish';

import { useDietaryRestrictions } from '@order/components';
import {
  CustomizationContainer,
  CustomizationCTA,
  CustomizationDetailsCompoundContainer,
  CustomizationIngredientsGrid,
  CustomizationSelectedIngredients,
} from '@order/features/customization';

import { useFilteredIngredientsModifications } from '../../../../hooks';
import { useProductDetailsScreenContext } from '../../../../ProductDetailsScreen.provider';
import {
  checkIfDressingKind,
  checkIfMaxModificationLimitExceeded,
  getActiveIngredientsModifications,
} from '../../../../state';
import type { IngredientModificationWithQuantity } from '../../../../types';
import {
  getFormattedQuantityByKind,
  getSelectedQuantityByKind,
} from '../../helpers';
import { ProductDetailsIngredientCard } from '../ProductDetailsIngredientCard';
import { ProductDetailsContentNavigationHeader } from '../ProductDetailsNavigationHeader';
import {
  useAddedIngredientId,
  useCustomizationTelemetry,
  useStepperTimeout,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsIngredientsViewSM = () => {
  const {
    addressId,
    restaurantSlug,
    product,
    modifications,
    quantity,
    activeIngredientsModificationsMap,
    addIngredientModification,
    removeIngredientModification,
    completelyRemoveIngredientModification,
    finishCustomization,
  } = useProductDetailsScreenContext();

  const addedIngredientId = useAddedIngredientId();

  const ingredientModifications = product?.ingredientModifications;

  const { calories, netPriceChange, active, limitations, maxModifications } =
    modifications;

  const hasMaxModifications = checkIfMaxModificationLimitExceeded({
    ...modifications,
    maxModifications:
      maxModifications > 0 ? maxModifications - 1 : maxModifications,
  });

  const getRestrictionNotice = useDietaryRestrictions({ referrer: 'ProductDetails' }); // prettier-ignore

  const { activeStepperId, setActiveStepperId, resetStepperTimeout } =
    useStepperTimeout();

  // ─── Refs ────────────────────────────────────────────────────────────

  const detailsContainerRef = useRef<ScrollView>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const [activeIngredientId, setActiveIngredientId] = useState<string>();

  const clearActiveIngredientId = useCallback(() => {
    setActiveIngredientId(undefined);
  }, []);

  // ─── Data ────────────────────────────────────────────────────────────

  const productCost = (product?.cost ?? 0) + netPriceChange;

  const activeIngredientModifications = useMemo(
    () => getActiveIngredientsModifications(modifications),
    [modifications],
  );

  const { ingredientsModificationsKinds, ingredientsModifications } =
    useFilteredIngredientsModifications(ingredientModifications);

  const { helpers, activeTargetId, setActiveTargetId } = useScrollspy({
    defaultActiveTargetId: ingredientsModificationsKinds[0],
  });

  // ─── Styles ──────────────────────────────────────────────────────────

  const selectedIngredientsScrollSV = useSharedValue(0);

  const updateIngredientsScrollSV = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      // eslint-disable-next-line functional/immutable-data
      selectedIngredientsScrollSV.value = event.nativeEvent.contentOffset.y;
    },
    [selectedIngredientsScrollSV],
  );

  // ─── Telemetry ───────────────────────────────────────────────────────

  const {
    trackIngredientsTabIngredientAdded,
    trackIngredientsTabIngredientRemoved,
    trackReviewTabIngredientAdded,
    trackReviewTabIngredientRemoved,
  } = useCustomizationTelemetry();

  const addIngredientFromIngredientsTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      addIngredientModification(ingredientModification);
      trackIngredientsTabIngredientAdded();
    },
    [addIngredientModification, trackIngredientsTabIngredientAdded],
  );

  const removeIngredientFromIngredientsTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      removeIngredientModification(ingredientModification);
      trackIngredientsTabIngredientRemoved();
    },
    [removeIngredientModification, trackIngredientsTabIngredientRemoved],
  );

  const addIngredientFromReviewTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      trackReviewTabIngredientAdded();

      return addIngredientModification(ingredientModification);
    },
    [addIngredientModification, trackReviewTabIngredientAdded],
  );

  const removeIngredientFromReviewTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      trackReviewTabIngredientRemoved();

      return removeIngredientModification(ingredientModification);
    },
    [removeIngredientModification, trackReviewTabIngredientRemoved],
  );

  const removeIngredientCompletelyFromReviewTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      completelyRemoveIngredientModification(ingredientModification);
      trackReviewTabIngredientRemoved();
    },
    [completelyRemoveIngredientModification, trackReviewTabIngredientRemoved],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!product) return null;

  return (
    <CustomizationContainer>
      <CustomizationDetailsCompoundContainer.Aside>
        <CustomizationDetailsCompoundContainer.ScrollView
          style={styles.container}
          ref={detailsContainerRef}
          stickyHeaderIndices={[0]}
          onScroll={updateIngredientsScrollSV}
          scrollEventThrottle={16}
          bounces={false}
        >
          <ProductDetailsContentNavigationHeader
            style={styles.header}
            scrollOffsetSV={selectedIngredientsScrollSV}
            restaurantSlug={restaurantSlug}
            addressId={addressId}
            text={product.name}
          />

          <CustomizationSelectedIngredients.Container
            wrapperStyle={styles.reviewContainer}
          >
            <CustomizationSelectedIngredients.List>
              {ingredientsModificationsKinds.map(
                (ingredientKindName, index) => {
                  const formattedQuantity = getFormattedQuantityByKind({
                    active,
                    ingredientsModifications,
                    ingredientKindName,
                  });

                  const isLastSection =
                    index === ingredientsModificationsKinds.length - 1;

                  const ingredientsForThisSection =
                    activeIngredientModifications.filter(
                      (ingredientModification) =>
                        ingredientModification.kind === ingredientKindName,
                    );

                  return (
                    <CustomizationSelectedIngredients.IngredientKindSection
                      key={ingredientKindName}
                      ingredientKindName={ingredientKindName}
                      style={isLastSection ? styles.withoutBorder : null}
                      formattedQuantity={formattedQuantity}
                    >
                      {ingredientsForThisSection.map(
                        (ingredientModification) => {
                          const isActive =
                            activeIngredientId ===
                            ingredientModification.ingredient.id;

                          const isDressing = checkIfDressingKind(
                            ingredientModification.kind,
                          );

                          const restrictionNotice = getRestrictionNotice(
                            ingredientModification?.ingredient
                              ?.dietaryProperties,
                          );

                          const shouldScrollToSelf =
                            addedIngredientId ===
                            ingredientModification.ingredient.id;

                          return (
                            <CustomizationSelectedIngredients.ListItem
                              key={ingredientModification.ingredient.id}
                              isActive={isActive}
                              shouldScrollToSelf={shouldScrollToSelf}
                              scrollViewRef={detailsContainerRef}
                            >
                              <CustomizationSelectedIngredients.Ingredient<IngredientModificationWithQuantity>
                                ingredientModification={ingredientModification}
                                isActive={isActive}
                                isDisabled={isDressing}
                                restrictionNotice={restrictionNotice}
                                setActive={setActiveIngredientId}
                                setInactive={clearActiveIngredientId}
                                onIncreaseQuantity={addIngredientFromReviewTab}
                                onDecreaseQuantity={removeIngredientFromReviewTab} // prettier-ignore
                                onRemove={removeIngredientCompletelyFromReviewTab} // prettier-ignore
                              />
                            </CustomizationSelectedIngredients.ListItem>
                          );
                        },
                      )}
                    </CustomizationSelectedIngredients.IngredientKindSection>
                  );
                },
              )}
            </CustomizationSelectedIngredients.List>
          </CustomizationSelectedIngredients.Container>
        </CustomizationDetailsCompoundContainer.ScrollView>

        <CustomizationCTA
          variation="continue"
          cost={productCost}
          quantity={quantity}
          calories={calories}
          onPress={finishCustomization}
        />
      </CustomizationDetailsCompoundContainer.Aside>

      {ingredientsModificationsKinds.length > 0 ? (
        <View style={styles.ingredientsGridContainer}>
          <CustomizationIngredientsGrid.Nav
            register={helpers.navScrollView.register}
            deregister={helpers.navScrollView.deregister}
            trackScroll={helpers.navScrollView.trackScroll}
            storeSize={helpers.navScrollView.storeSize}
          >
            {ingredientsModificationsKinds.map((kind) => (
              <CustomizationIngredientsGrid.NavItem
                key={kind}
                isActive={activeTargetId === kind}
                onPress={setActiveTargetId}
                targetId={kind}
                ingredientKindName={kind}
                register={helpers.navItem.register}
                deregister={helpers.navItem.deregister}
              />
            ))}
          </CustomizationIngredientsGrid.Nav>

          <CustomizationIngredientsGrid.Container
            register={helpers.scrollView.register}
            deregister={helpers.scrollView.deregister}
            storeContentSize={helpers.scrollView.storeContentSize}
            storeSize={helpers.scrollView.storeSize}
            trackScroll={helpers.scrollView.trackScroll}
          >
            {Object.entries(ingredientsModifications).map(
              ([ingredientKindName, ingredientKind]) => (
                <CustomizationIngredientsGrid.Section
                  key={ingredientKindName}
                  ingredientKindName={ingredientKindName}
                  register={helpers.target.register}
                  deregister={helpers.target.deregister}
                >
                  <CustomizationIngredientsGrid.SectionStack>
                    {ingredientKind.modifications.map((modification) => {
                      const { ingredient } = modification;

                      const maybeActiveIngredient =
                        activeIngredientsModificationsMap.get(ingredient.id);
                      const activeIngredientQuantity =
                        maybeActiveIngredient?.quantity ?? 0;

                      const dietaryRestrictionNote = getRestrictionNotice(
                        ingredient?.dietaryProperties,
                      );

                      const selectedQuantity = getSelectedQuantityByKind({
                        active,
                        ingredientKindName,
                      });

                      const maxQuantity =
                        limitations?.[ingredientKindName]?.max ??
                        Number.POSITIVE_INFINITY;

                      const isAtMaxQuantity =
                        selectedQuantity >= maxQuantity || hasMaxModifications;

                      return (
                        <ProductDetailsIngredientCard
                          palette="spinach"
                          key={ingredient.id}
                          ingredientModification={modification}
                          quantity={activeIngredientQuantity}
                          dietaryRestrictionNote={dietaryRestrictionNote}
                          activeStepperId={activeStepperId}
                          isAtMaxQuantity={isAtMaxQuantity}
                          onIncrement={addIngredientFromIngredientsTab}
                          onDecrement={removeIngredientFromIngredientsTab}
                          setActiveStepperId={setActiveStepperId}
                          resetStepperTimeout={resetStepperTimeout}
                        />
                      );
                    })}
                  </CustomizationIngredientsGrid.SectionStack>
                </CustomizationIngredientsGrid.Section>
              ),
            )}
          </CustomizationIngredientsGrid.Container>
        </View>
      ) : null}
    </CustomizationContainer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: 0,
  },
  header: {
    width: '100%',
    flexDirection: 'row',
    paddingVertical: theme.spacing['1'],
    marginBottom: theme.spacing['8'],
  },
  reviewContainer: {
    marginHorizontal: -theme.spacing['4'],
  },
  ingredientsGridContainer: {
    flex: 1,
  },
  withoutBorder: {
    borderBottomWidth: 0,
  },
});
